/** New version of admin details to phase out view page/review pages created one off for various record types */
import { Alert, AlertProps, Dropdown, DropdownItem, Tabs } from '@platform/components';
import { AdminTableProvider } from './AdminTable';
import { Link } from 'wouter';
import { MenuSection, Header, Separator } from 'react-aria-components';

type DetailsPageProps = {
  tabs: { id: string; content: React.ReactNode }[];
  title: string;
  alerts?: AlertProps[];
  pageActions?: DetailPageAction[];
  sectionedPageActions?: SectionedPageActionsProps[];
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  previewLink?: string;
};

export const AdminDetailPage = ({
  title,
  tabs,
  pageActions,
  sectionedPageActions,
  isLoading,
  isError,
  isSuccess,
  previewLink,
  alerts,
}: DetailsPageProps) => {
  return (
    // Some tabs may contain simplified admin tables, provider allows sort/pagination state to be wired in
    <AdminTableProvider>
      <div className='px-14 text-gray-500'>
        <div className='flex justify-between items-center mb-4'>
          <h1 className='text-display-sm font-semibold'>
            {title}
            {previewLink && (
              <span className='ml-2 text-sm text-gray-400'>
                [
                <Link className='text-brand-500' to={previewLink}>
                  preview
                </Link>
                ]
              </span>
            )}
          </h1>
          {sectionedPageActions ?
            <SectionedPageActions actions={sectionedPageActions} />
          : <PageActions actions={pageActions || []} />}
        </div>
        {alerts && (
          <div className='w-full mx-auto'>
            {alerts.map((alert, index) => (
              <Alert additonalClassnames=' my-2' key={index} {...alert} />
            ))}
          </div>
        )}
        {isLoading && <div>Loading...</div>}
        {isError && <div>Error loading data.</div>}
        {isSuccess && <Tabs title={`${title} Details`} items={tabs} variant='side-tabs' />}
      </div>
    </AdminTableProvider>
  );
};

export type DetailPageAction =
  | {
      type: 'link';
      text: string;
      href: string;
      isDanger?: boolean;
    }
  | {
      type: 'button';
      text: string;
      onPress: () => void;
      isDanger?: boolean;
    };

const PageActions = ({ actions }: { actions: DetailPageAction[] }) => {
  return (
    <Dropdown
      type='button'
      buttonProps={{ children: 'Actions', variant: 'tertiary-gray', size: 'sm' }}
      ariaLabel='Page actions'
    >
      {actions.map((action, index) =>
        action.type === 'button' ?
          <DropdownItem
            key={index}
            onAction={() => action.onPress()}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            {action.text}
          </DropdownItem>
        : <DropdownItem
            key={index}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            <Link to={action.href} className='block w-full h-full'>
              {action.text}
            </Link>
          </DropdownItem>,
      )}
    </Dropdown>
  );
};

export type SectionedPageActionsProps = {
  header: string;
  actions: DetailPageAction[];
};

export const SectionedPageActions = ({ actions }: { actions: SectionedPageActionsProps[] }) => {
  return (
    <Dropdown
      type='button'
      buttonProps={{ children: 'Actions', variant: 'tertiary-gray', size: 'sm' }}
      ariaLabel='Page actions'
    >
      {actions.map((section, index) => (
        <MenuSection key={index} className='mb-2'>
          {index !== 0 && <Separator />}
          <Header className='text-gray-400 mx-3 mt-2 uppercase text-xs'>{section.header}</Header>
          {section.actions.map((action, actionIndex) =>
            action.type === 'button' ?
              <DropdownItem
                key={actionIndex}
                onAction={() => action.onPress()}
                additionalClassnames={
                  action.isDanger ? 'text-error-600 font-semibold text-xs' : 'text-xs'
                }
                compact={true}
              >
                {action.text}
              </DropdownItem>
            : <DropdownItem
                key={actionIndex}
                additionalClassnames={
                  action.isDanger ? 'text-error-600 font-semibold text-xs' : 'text-xs'
                }
                compact={true}
              >
                <Link to={action.href} className='block w-full h-full'>
                  {action.text}
                </Link>
              </DropdownItem>,
          )}
        </MenuSection>
      ))}
    </Dropdown>
  );
};
